import React from 'react';
import './StreamGenerationProgress.css';

interface StreamGenerationProgressProps {
    streamNames: string[];
    currentStreamIndex: number;
    totalStreams: number;
    isGenerating: boolean;
    error?: string | null;
    onExport?: () => void;
}

const StreamGenerationProgress: React.FC<StreamGenerationProgressProps> = ({
    streamNames,
    currentStreamIndex,
    totalStreams,
    isGenerating,
    error,
    onExport
}) => {
    const progress = totalStreams > 0 ? (currentStreamIndex / totalStreams) * 100 : 0;
    const isComplete = !isGenerating && currentStreamIndex === totalStreams && !error;

    return (
        <div className="stream-generation-progress">
            <div className="progress-header">
                <h3>Generating Content Streams</h3>
                {isGenerating && (
                    <div className="spinner"></div>
                )}
            </div>

            {error && (
                <div className="progress-error">
                    {error}
                </div>
            )}

            <div className="progress-bar-container">
                <div className="progress-bar-track">
                    <div
                        className="progress-bar-fill"
                        style={{ width: `${progress}%` }}
                    ></div>
                </div>
                <div className="progress-text">
                    {currentStreamIndex} of {totalStreams} streams generated
                </div>
            </div>

            <div className="stream-list">
                {streamNames.map((streamName, index) => {
                    const status = index < currentStreamIndex
                        ? 'completed'
                        : index === currentStreamIndex
                        ? 'in-progress'
                        : 'pending';

                    return (
                        <div key={index} className={`stream-item stream-${status}`}>
                            <div className="stream-status-icon">
                                {status === 'completed' && (
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="10" r="9" stroke="#10b981" strokeWidth="2" fill="none" />
                                        <path d="M6 10l3 3 5-6" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                                {status === 'in-progress' && (
                                    <div className="stream-spinner"></div>
                                )}
                                {status === 'pending' && (
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <circle cx="10" cy="10" r="9" stroke="#d1d5db" strokeWidth="2" fill="none" />
                                    </svg>
                                )}
                            </div>
                            <div className="stream-name">{streamName}</div>
                            <div className="stream-status-text">
                                {status === 'completed' && 'Completed'}
                                {status === 'in-progress' && 'Generating...'}
                                {status === 'pending' && 'Pending'}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isComplete && onExport && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <div style={{
                        background: '#d1fae5',
                        border: '1px solid #6ee7b7',
                        padding: '15px',
                        borderRadius: '6px',
                        marginBottom: '20px',
                        color: '#065f46'
                    }}>
                        <strong>Masterplan generated successfully!</strong> You can now export it as an HTML document.
                    </div>
                    <button
                        onClick={onExport}
                        style={{
                            padding: '12px 24px',
                            background: '#10b981',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '0.875rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
                        onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
                    >
                        Download HTML Export
                    </button>
                </div>
            )}
        </div>
    );
};

export default StreamGenerationProgress;
